import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/api-client'
import { getPeakPricingRules, createPeakPricingRule, updatePeakPricingRule, deletePeakPricingRule } from '@/services/bookingApi'
import type { PeakPricingRule, Service } from '@/types'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Loader2, Plus, Edit2, Trash2, X, Flame, Snowflake } from 'lucide-react'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

interface ServiceOption {
  id: string
  name: string
}

const DAYS_OF_WEEK = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
}

export default function PeakPricingEditor() {
  const embedded = useDashboardEmbedded()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingRule, setEditingRule] = useState<PeakPricingRule | null>(null)

  // Fetch peak pricing rules
  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['peak-pricing-rules'],
    queryFn: getPeakPricingRules,
  })

  // Fetch services for dropdown
  const { data: servicesData } = useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { categories: { id: string; name: string; services: Service[] }[]; uncategorized: Service[] }
      }>('/user/services')
      return response.data.data
    },
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createPeakPricingRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peak-pricing-rules'] })
      setShowForm(false)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: Partial<PeakPricingRule> & { id: string }) =>
      updatePeakPricingRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peak-pricing-rules'] })
      setEditingRule(null)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deletePeakPricingRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peak-pricing-rules'] })
    },
  })

  // Group rules by type
  const peakRules = rules.filter((rule) => !rule.is_off_peak)
  const offPeakRules = rules.filter((rule) => rule.is_off_peak)

  // Build service options list
  const serviceOptions: ServiceOption[] = []
  if (servicesData) {
    servicesData.categories.forEach((cat) => {
      cat.services.forEach((svc) => {
        serviceOptions.push({ id: svc.id, name: svc.name })
      })
    })
    servicesData.uncategorized.forEach((svc) => {
      serviceOptions.push({ id: svc.id, name: svc.name })
    })
  }

  const getServiceName = (serviceId: string | null): string => {
    if (!serviceId) return 'All services'
    const service = serviceOptions.find((s) => s.id === serviceId)
    return service?.name || 'Unknown service'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8">
      {!embedded && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Peak & Off-Peak Pricing</h1>
          <p className="text-gray-500 mt-1">
            Configure pricing rules to apply markups during peak times or discounts during slower periods.
          </p>
        </div>
      )}

      {/* Peak Pricing Rules Section */}
      <Card className="mb-6 border-orange-200 bg-orange-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Flame className="w-5 h-5" />
            Peak Pricing Rules (Markup)
          </CardTitle>
          <CardDescription>
            These rules increase prices during high-demand periods
          </CardDescription>
        </CardHeader>
        <CardContent>
          {peakRules.length === 0 ? (
            <p className="text-gray-500 text-sm py-4">No peak pricing rules configured.</p>
          ) : (
            <div className="space-y-3">
              {peakRules.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  serviceName={getServiceName(rule.service_id)}
                  onEdit={() => setEditingRule(rule)}
                  onDelete={() => deleteMutation.mutate(rule.id)}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Off-Peak Pricing Rules Section */}
      <Card className="mb-6 border-blue-200 bg-blue-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <Snowflake className="w-5 h-5" />
            Off-Peak Pricing Rules (Discount)
          </CardTitle>
          <CardDescription>
            These rules reduce prices during slower periods to attract more bookings
          </CardDescription>
        </CardHeader>
        <CardContent>
          {offPeakRules.length === 0 ? (
            <p className="text-gray-500 text-sm py-4">No off-peak pricing rules configured.</p>
          ) : (
            <div className="space-y-3">
              {offPeakRules.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  serviceName={getServiceName(rule.service_id)}
                  onEdit={() => setEditingRule(rule)}
                  onDelete={() => deleteMutation.mutate(rule.id)}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Button */}
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Pricing Rule
        </Button>
      </div>

      {/* Add/Edit Modal */}
      {(showForm || editingRule) && (
        <PricingRuleFormModal
          rule={editingRule}
          serviceOptions={serviceOptions}
          onSubmit={async (data) => {
            if (editingRule) {
              await updateMutation.mutateAsync({ id: editingRule.id, ...data })
            } else {
              await createMutation.mutateAsync(data)
            }
          }}
          onClose={() => {
            setShowForm(false)
            setEditingRule(null)
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  )
}

interface RuleCardProps {
  rule: PeakPricingRule
  serviceName: string
  onEdit: () => void
  onDelete: () => void
  isDeleting: boolean
}

function RuleCard({ rule, serviceName, onEdit, onDelete, isDeleting }: RuleCardProps) {
  const { t } = useTranslation('dashboard')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const isOffPeak = rule.is_off_peak
  const borderColor = isOffPeak ? 'border-blue-200' : 'border-orange-200'
  const bgColor = isOffPeak ? 'bg-blue-50' : 'bg-orange-50'
  const iconColor = isOffPeak ? 'text-blue-500' : 'text-orange-500'
  const badgeColor = isOffPeak
    ? 'bg-blue-100 text-blue-700'
    : 'bg-orange-100 text-orange-700'

  const formatMultiplier = (multiplier: number, isOffPeak: boolean): string => {
    const percentage = Math.round((multiplier - 1) * 100)
    if (isOffPeak) {
      return `${percentage}% off`
    }
    return `+${percentage}%`
  }

  const formatTimeRange = (start: string, end: string): string => {
    return `${start} - ${end}`
  }

  return (
    <Card className={`${borderColor} border-l-4`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-full ${bgColor}`}>
              {isOffPeak ? (
                <Snowflake className={`w-4 h-4 ${iconColor}`} />
              ) : (
                <Flame className={`w-4 h-4 ${iconColor}`} />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900">
                  {DAYS_OF_WEEK[rule.day_of_week as keyof typeof DAYS_OF_WEEK]}
                </span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-600">{formatTimeRange(rule.start_time, rule.end_time)}</span>
                <span className={`px-2 py-0.5 text-sm font-medium rounded ${badgeColor}`}>
                  {formatMultiplier(rule.multiplier, isOffPeak)}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                <span className={rule.service_id ? 'text-gray-700' : ''}>{serviceName}</span>
                {rule.label && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span>{rule.label}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onEdit}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            {showDeleteConfirm ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                >
                  {t('services.delete.delete')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                >
                  {t('services.delete.cancel')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface PricingRuleFormModalProps {
  rule: PeakPricingRule | null
  serviceOptions: ServiceOption[]
  onSubmit: (data: {
    service_id?: string
    day_of_week: number
    start_time: string
    end_time: string
    multiplier: number
    is_off_peak: boolean
    label?: string
  }) => Promise<void>
  onClose: () => void
  isLoading: boolean
}

function PricingRuleFormModal({
  rule,
  serviceOptions,
  onSubmit,
  onClose,
  isLoading,
}: PricingRuleFormModalProps) {
  const [serviceId, setServiceId] = useState(rule?.service_id || '')
  const [dayOfWeek, setDayOfWeek] = useState(rule?.day_of_week?.toString() || '0')
  const [startTime, setStartTime] = useState(rule?.start_time || '09:00')
  const [endTime, setEndTime] = useState(rule?.end_time || '17:00')
  const [multiplier, setMultiplier] = useState(rule?.multiplier?.toString() || '1.25')
  const [isOffPeak, setIsOffPeak] = useState(rule?.is_off_peak ?? false)
  const [label, setLabel] = useState(rule?.label || '')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({
      service_id: serviceId || undefined,
      day_of_week: parseInt(dayOfWeek, 10),
      start_time: startTime,
      end_time: endTime,
      multiplier: parseFloat(multiplier),
      is_off_peak: isOffPeak,
      label: label || undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">
            {rule ? 'Edit Pricing Rule' : 'Add Pricing Rule'}
          </h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Rule Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rule Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsOffPeak(false)}
                className={`flex-1 py-2 px-3 rounded-lg border transition-colors ${
                  !isOffPeak
                    ? 'bg-orange-50 border-orange-300 text-orange-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Flame className={`w-4 h-4 mx-auto mb-1 ${!isOffPeak ? 'text-orange-500' : 'text-gray-400'}`} />
                Peak
              </button>
              <button
                type="button"
                onClick={() => setIsOffPeak(true)}
                className={`flex-1 py-2 px-3 rounded-lg border transition-colors ${
                  isOffPeak
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Snowflake className={`w-4 h-4 mx-auto mb-1 ${isOffPeak ? 'text-blue-500' : 'text-gray-400'}`} />
                Off-Peak
              </button>
            </div>
          </div>

          {/* Service Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
            <select
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">All services (global)</option>
              {serviceOptions.map((svc) => (
                <option key={svc.id} value={svc.id}>
                  {svc.name}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Leave empty to apply this rule to all services
            </p>
          </div>

          {/* Day of Week */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              required
            >
              {Object.entries(DAYS_OF_WEEK).map(([value, name]) => (
                <option key={value} value={value}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Time Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Multiplier */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isOffPeak ? 'Discount Multiplier' : 'Markup Multiplier'}
            </label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={multiplier}
              onChange={(e) => setMultiplier(e.target.value)}
              placeholder={isOffPeak ? '0.85 for 15% off' : '1.25 for 25% markup'}
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              {isOffPeak
                ? 'Enter a number less than 1 (e.g., 0.85 = 15% discount)'
                : 'Enter a number greater than 1 (e.g., 1.25 = 25% markup)'}
            </p>
          </div>

          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label (optional)
            </label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Weekend premium, Summer special"
            />
            <p className="text-sm text-gray-500 mt-1">
              A descriptive label to help you identify this rule
            </p>
          </div>

          {/* Preview */}
          <div className={`p-3 rounded-lg ${isOffPeak ? 'bg-blue-50' : 'bg-orange-50'}`}>
            <p className="text-sm font-medium text-gray-700">Preview:</p>
            <p className={`text-sm ${isOffPeak ? 'text-blue-700' : 'text-orange-700'}`}>
              {DAYS_OF_WEEK[parseInt(dayOfWeek) as keyof typeof DAYS_OF_WEEK]}{' '}
              {startTime} - {endTime}:{' '}
              <span className="font-semibold">
                {isOffPeak
                  ? `${Math.round((1 - parseFloat(multiplier)) * 100)}% off`
                  : `+${Math.round((parseFloat(multiplier) - 1) * 100)}%`}
              </span>
              {serviceId && ` on ${serviceOptions.find((s) => s.id === serviceId)?.name}`}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : rule ? (
                'Update Rule'
              ) : (
                'Create Rule'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
